import { useQuery } from '@tanstack/react-query';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import {
  getActiveStatusFilterField,
  getDateFromToFilterFields,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { promoAssignmentsInfiniteQueryOptions, promoItemsQueryOptions } from '../api/promotion.api';
import { getAssignmentColumns } from '../components/PromoColumns';
import { ACTIVE_ROWS_LOOKUP_PARAMS } from '../constants/promotion.constants';
import { usePromoAssignmentFilters } from '../hooks/usePromoAssignmentFilters';
import { getPromoItemFilterField } from '../utils/promotionFilterFields';
import { getUserPromoStatCards } from '../utils/promotionStatCards';

export function PromoDistributionPage() {
  const controller = usePromoAssignmentFilters();
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(promoAssignmentsInfiniteQueryOptions(controller.params));
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());
  const { data: items } = useQuery(promoItemsQueryOptions(ACTIVE_ROWS_LOOKUP_PARAMS));

  return (
    <CatalogueListLayout
      title="Promotional Item Distribution"
      dateRange={{ dateFrom: controller.params.dateFrom, dateTo: controller.params.dateTo }}
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search by name or HR id',
      }}
      filters={controller}
      filterFields={[
        ...getDateFromToFilterFields(),
        getZonalManagerFilterField(zonalManagers),
        getPromoItemFilterField(items?.data),
        getActiveStatusFilterField(),
      ]}
      stats={{
        cards: getUserPromoStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the assignments"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={getAssignmentColumns()}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="Nothing has been assigned"
          {...loadMoreProps}
        />
      )}
    </CatalogueListLayout>
  );
}
